import getDate from 'date-fns/getDate';
import getMonth from 'date-fns/getMonth';
import getYear from 'date-fns/getYear';
import { match } from 'path-to-regexp';
import { useEffect } from 'react';

import { REDUX_STORE } from '../../../constants/common';
import { MATERIAL, NEWS } from '../../../constants/routes';
import { getMaterialLink } from '../../../helpers/news';
import { IMaterial, IRouterQuery } from '../../../interfaces/news';
import { selectMaterialByAlias, selectPlacementEndlessScroll, setCurrentArticleIndex } from '../../../reducers/news';
import { getViewedArticles } from '../../../selectors/news';
import { isServer } from '../../../utils/isServer';

type TLoadMore = () => Promise<void>;
export type TIncreaseVisit = (material: IMaterial) => Promise<void>;
export type TChangeUrl = (material: IMaterial) => void;
type TRouteParams = Omit<IRouterQuery, 'rubricAlias' | 'tag' | 'rubric'>;

let loadedCache: Record<string, string> = {};

const findEntryByTrigger = (trigger: string, entries: IntersectionObserverEntry[]) =>
  //@ts-ignore
  entries.find((entry) => entry.target.dataset.trigger === trigger);

export const createIntersectionObserverHook = () => {
  let observer: IntersectionObserver;

  const useIntersectionObserver = (...handlers: IntersectionObserverCallback[]) => {
    if (!observer && !isServer) {
      observer = new IntersectionObserver(
        (...args) => {
          handlers.forEach((handle) => handle(...args));
        },
        {
          // rootMargin: `0px 0px -${window.innerHeight}px 0px`,
          rootMargin: `0px 0px 0px 0px`,
        },
      );
    }

    useEffect(() => {
      return () => {
        observer.disconnect();
        observer = null;
      };
    }, []);

    return observer;
  };

  return useIntersectionObserver;
};

export const newsMatcher = match<TRouteParams>(NEWS);
export const materialMatcher = match<TRouteParams>(MATERIAL);

export const getCurrentRouteParams = (): TRouteParams => {
  const news = newsMatcher(window.location.pathname);
  const material = materialMatcher(window.location.pathname);
  return (news ? news?.params : material ? material.params : {}) as TRouteParams;
};

export const getDateForUrl = (publishDate: string) => {
  const date = new Date(publishDate);

  return {
    year: getYear(date),
    month: getMonth(date) + 1,
    day: getDate(date),
  };
};

const useInfiniteWithVisiterAndChangeUrlScrollHook = createIntersectionObserverHook();

const createLoadingMoreHandler = (loadMore: TLoadMore): IntersectionObserverCallback => {
  return (entries) => {
    const { getState } = window[REDUX_STORE];

    const { alias } = getCurrentRouteParams();

    const articleTriggerEntry = findEntryByTrigger('loading', entries);

    if (articleTriggerEntry) {
      // @ts-ignore
      const articleAlias = articleTriggerEntry.target?.dataset?.alias;
      const material = selectMaterialByAlias(getState())(articleAlias);
      if (articleTriggerEntry?.isIntersecting) {
        if (!loadedCache[articleAlias]) {
          loadMore();
          loadedCache[articleAlias] = articleAlias;
        }
        if (articleAlias !== alias) {
          changeUrlByExternalMaterial(material);
        }
      } else {
      }
    }
  };
};

const changeUrlByExternalMaterial: TChangeUrl = (material: IMaterial) => {
  const { dispatch } = window[REDUX_STORE];

  const { title } = material;
  document.title = title.replace(/<img[^>]+\>/i, '');

  dispatch(setCurrentArticleIndex(material.alias));

  toggleGetParamsEndlessScroll(material.alias);

  const { as } = getMaterialLink(material);
  const url = as.split('?')[0];
  window.history.pushState({ alias: material.alias }, null, `${url}${window.location.search}`);
};

const createChangeUrlAndIncreaseVisitHandler = ({
  changeUrl,
  increaseVisit,
}: {
  changeUrl: TChangeUrl;
  increaseVisit: TIncreaseVisit;
}): IntersectionObserverCallback => (entries) => {
  const { getState } = window[REDUX_STORE];
  const { alias } = getCurrentRouteParams();

  const visitTriggerEntry = findEntryByTrigger('visiter', entries);

  if (visitTriggerEntry) {
    // @ts-ignore
    const articleAlias = visitTriggerEntry.target.dataset?.alias;

    if (visitTriggerEntry?.isIntersecting) {
      const material = selectMaterialByAlias(getState())(articleAlias);

      if (articleAlias !== alias) {
        changeUrl(material);
      }

      if (!getViewedArticles(getState()).includes(material.id)) {
        increaseVisit(material);
      }

      if (articleAlias === alias) {
        const articlesList = Array.from(document.querySelectorAll('[data-trigger=visiter]'));
        const index = articlesList.findIndex((item) => item === visitTriggerEntry.target);
        const prevArticle = articlesList[index - 1];

        // @ts-ignore
        const prevArticleAlias = prevArticle?.dataset?.alias;

        if (visitTriggerEntry.boundingClientRect.top > window.innerHeight) {
          const material = selectMaterialByAlias(getState())(prevArticleAlias);
          changeUrl(material);
        }
      }
    } else {
    }
  }
};

const intSource = 'int_source';
const intMedium = 'int_medium';
const intSourceForPlacement = 'sravni.ru';
const intMediumForPlacement = 'bs';

export const toggleGetParamsEndlessScroll = (articleAlias: string) => {
  const { getState } = window[REDUX_STORE];
  const materialPlacementEndlessScroll = selectPlacementEndlessScroll(getState())(articleAlias);
  const articlesList = Array.from(document.querySelectorAll('[data-trigger=visiter]'));
  const url = new URL(window.location.href);

  const intSourceParam = url.searchParams.get(intSource);
  const intMediumParam = url.searchParams.get(intMedium);

  if (
    materialPlacementEndlessScroll?.alias === articleAlias &&
    materialPlacementEndlessScroll?.alias === articlesList[1]?.id &&
    intSourceParam === null &&
    intMediumParam === null
  ) {
    // если это плейсмент с пустыми источниками
    url.searchParams.append(intSource, intSourceForPlacement);
    url.searchParams.append(intMedium, intMediumForPlacement);
    window.history.pushState(null, null, url);
  } else {
    if (intSourceParam !== intSourceForPlacement && intMediumParam !== intMediumForPlacement) return;
    // если остались наши метки плейсмента (бс, сравни.ру)
    url.searchParams.delete(intSource);
    url.searchParams.delete(intMedium);
    window.history.replaceState(null, null, url);
  }
};

export const useInfiniteWithVisiterAndChangeUrlScroll = ({
  loadMore,
  increaseVisit,
  changeUrl,
  initialCache,
}: {
  loadMore: TLoadMore;
  increaseVisit: TIncreaseVisit;
  changeUrl: TChangeUrl;
  initialCache?: Record<string, string>;
}) => {
  useEffect(() => {
    loadedCache = initialCache || {};
    return () => {
      loadedCache = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return useInfiniteWithVisiterAndChangeUrlScrollHook(
    createLoadingMoreHandler(loadMore),
    createChangeUrlAndIncreaseVisitHandler({ increaseVisit, changeUrl }),
  );
};
